/**
 * 顶部工具条：麦克风、摄像头、屏幕共享、录制、房间模式。
 */

import { useEffect, useRef, useState } from 'react'

import type { Client } from '../lib/client'
import { hasPerm, MIC_NONE, Perm, RoomFlag, UState } from '../lib/protocol'
import { mediaBlockedReason } from '../lib/media'
import { useHotkeys } from '../lib/hotkeys'
import { DeviceSettings } from './DeviceSettings'
import { startCaptionCapture, type CaptionCapture } from '../lib/captionAudio'
import { apiURL } from '../lib/endpoint'
import { BIN_KIND_CAPTION_AUDIO } from '../lib/messages'
import { GroupCompose } from './Groups'
import { MeetingControl } from './MeetingControl'
import { PollCompose } from './PollDialogs'
import { RoomInfo } from './RoomInfo'
import { ReactionPicker } from './Reactions'
import { useRoomStore } from '../stores/roomStore'
import { isAnnotPage, useWhiteboardStore } from '../stores/whiteboardStore'
import { PALETTE, type WBTool } from '../lib/whiteboard-types'
import {
  IcCam, IcCamOff, IcEllipse, IcEraser, IcHand, IcHangup, IcLine, IcMic, IcMicOff,
  IcMore, IcPause, IcPen, IcPlay, IcRecord, IcRecordStop, IcRect, IcSelect,
  IcShare, IcSpeaker, IcSpeakerOff, IcStop, IcText,
} from './Icons'
import { AttendStartDialog } from './Attend'
import { useMobileLayout } from '../lib/mobile'
import { useAudioSession } from '../lib/useAudioSession'
import { BottomSheet } from './BottomSheet'
import { HotkeyHelp } from './HotkeyHelp'
import { EMOJIS } from './Reactions'
import { IcChat, IcUsers } from './Icons'

interface Props {
  /** 准备页选好的初始状态。进教室后自动按它开麦克风与摄像头。 */
  startWith?: { micOn: boolean; camOn: boolean }
  onLocalStream: (s: MediaStream | null) => void
  /** 退出教室。放在工具栏最右且涂红，与其它操作拉开距离。 */
  onLeave: () => void
  /** 进房密码，教室信息里展示；未设密码为空串 */
  roomPassword?: string
  /**
   * 手机布局下的抽屉入口。有它就按手机排：底栏只留五个，
   * 成员 / 聊天走抽屉，表情、录制、退出收进「更多」。桌面不传。
   */
  mobileSheets?: {
    openChat: () => void
    openMembers: () => void
    unread: { count: number; mention: boolean }
    memberCount: number
  }
  /**
   * 桌面布局下侧栏（成员 + 交流）的开关。腾讯会议的工具条上就有「成员」「聊天」两个按钮，
   * 用户找侧栏第一反应是往底栏看，而不是找右上角那个小箭头。手机不传。
   */
  desktopSide?: {
    open: boolean
    toggle: () => void
    openChat: () => void
    unread: { count: number; mention: boolean }
    memberCount: number
  }
}

const TOOLS: Array<{ id: WBTool; label: string; Icon: React.ComponentType }> = [
  { id: 'pen', label: '画笔', Icon: IcPen },
  { id: 'line', label: '直线', Icon: IcLine },
  { id: 'rect', label: '矩形', Icon: IcRect },
  { id: 'ellipse', label: '椭圆', Icon: IcEllipse },
  { id: 'text', label: '文字', Icon: IcText },
  { id: 'eraser', label: '橡皮', Icon: IcEraser },
  { id: 'select', label: '选择', Icon: IcSelect },
]

export function Toolbar({ startWith, onLocalStream, onLeave, roomPassword = '', mobileSheets, desktopSide }: Props) {
  const mobileBar = !!mobileSheets
  const media = useRoomStore((s) => s.media)
  const members = useRoomStore((s) => s.members)
  const selfId = useRoomStore((s) => s.selfId)
  const room = useRoomStore((s) => s.room)
  const recording = useRoomStore((s) => s.recording)
  const sharerId = useRoomStore((s) => s.sharerId)
  const requestMic = useRoomStore((s) => s.requestMic)
  const releaseMic = useRoomStore((s) => s.releaseMic)
  const startRecord = useRoomStore((s) => s.startRecord)
  const stopRecord = useRoomStore((s) => s.stopRecord)
  const startShare = useRoomStore((s) => s.startShare)
  const stopShare = useRoomStore((s) => s.stopShare)
  const pauseShare = useRoomStore((s) => s.pauseShare)
  const sharePaused = useRoomStore((s) => s.sharePaused)
  const away = useRoomStore((s) => s.away)
  const toggleAway = useRoomStore((s) => s.toggleAway)
  const setSelfInfo = useRoomStore((s) => s.setSelfInfo)
  const setRoomFlags = useRoomStore((s) => s.setRoomFlags)
  const setMicNum = useRoomStore((s) => s.setMicNum)

  const [micOn, setMicOn] = useState(false)
  const [camOn, setCamOn] = useState(false)
  // 「更多」里放的是一节课点不了几次的功能，收起来给常用操作让位。
  const [moreOpen, setMoreOpen] = useState(false)
  const [infoOpen, setInfoOpen] = useState(false)
  const [pollOpen, setPollOpen] = useState(false)
  const [attendOpen, setAttendOpen] = useState(false)
  const [groupOpen, setGroupOpen] = useState(false)
  const [mcOpen, setMcOpen] = useState(false)
  const captionOn = useRoomStore((s) => s.captionOn)
  const toggleCaption = useRoomStore((s) => s.toggleCaption)
  const capRef = useRef<CaptionCapture | null>(null)
  const client = useRoomStore((s) => s.client)
  // 服务端没装 whisper 模型时不显示字幕开关：给个点了没反应的按钮，
  // 比没有这个按钮更糟 —— 主持人会以为是自己操作错了。
  const [captionable, setCaptionable] = useState(false)
  useEffect(() => {
    let alive = true
    fetch(apiURL('/healthz'))
      .then((r) => r.json())
      .then((d) => { if (alive && d?.caption === true) setCaptionable(true) })
      .catch(() => { /* 取不到就当不支持 */ })
    return () => { alive = false }
  }, [])
  // 退出要确认一下：底栏最右边的按钮很容易误触，一点就掉线太狠。
  const [leaveOpen, setLeaveOpen] = useState(false)
  const sendReaction = useRoomStore((s) => s.sendReaction)
  const [devOpen, setDevOpen] = useState(false)
  const [hkOpen, setHkOpen] = useState(false)
  const [muted, setMuted] = useState(false)

  const me = members.get(selfId)
  const onMic = (me?.micId ?? MIC_NONE) >= 0
  const freeSpeak = !!room && (room.flags & RoomFlag.StateFreeSpeak) !== 0 &&
    (room.flags & (RoomFlag.StateOpMode | RoomFlag.StateLectureMode)) === 0
  const handRaised = ((me?.state ?? 0) & UState.HandRaised) !== 0
  const raiseHand = useRoomStore((s) => s.raiseHand)
  const canSpeak = !!me && hasPerm(me.prop, Perm.Speak)
  const canRecord = !!me && hasPerm(me.prop, Perm.Record)
  const canShare = !!me && hasPerm(me.prop, Perm.AppShare)
  const canSetRoom = !!me && hasPerm(me.prop, Perm.SetRoomProp)
  const canSetMicNum = !!me && hasPerm(me.prop, Perm.SetMicNum)

  // 进教室就把「这里用不了音视频」说清楚，而不是等他点了按钮才发现。
  // 上课前几分钟发现比上课中途发现要好得多。
  const mediaBlocked = mediaBlockedReason()
  const isSharing = sharerId === selfId

  // 字幕采集：只在「房间开着字幕」且「自己麦克风开着」时采。
  // 两个条件缺一就停 —— 关了麦还在传音频，等于背着用户录音。
  useEffect(() => {
    const want = captionOn && micOn && !!media
    if (!want) {
      capRef.current?.stop()
      capRef.current = null
      return
    }
    if (capRef.current) return
    let alive = true
    void (async () => {
      try {
        const stream = media!.localAudioStream()
        if (!stream) return
        const cap = await startCaptionCapture(stream, (pcm) => {
          // 直接发二进制：JSON base64 会让体积涨三分之一，而语音是持续流量。
          client?.sendBinary(BIN_KIND_CAPTION_AUDIO, {}, new Uint8Array(pcm.buffer))
        })
        if (!alive) { cap.stop(); return }
        capRef.current = cap
      } catch {
        // 采集起不来就算了，字幕本身是增强功能，不该影响上课。
      }
    })()
    return () => { alive = false }
  }, [captionOn, micOn, media, client])

  // 离开教室时务必停掉采集：AudioContext 有数量上限，泄漏几次后音频功能会静默失效。
  useEffect(() => () => { capRef.current?.stop(); capRef.current = null }, [])

  /** 开关麦克风推流。 */
  const toggleMic = async () => {
    if (!media) return
    if (micOn) {
      await media.unpublish(['audio'])
      setMicOn(false)
      // 关麦即结束发言，把状态交回去。
      if (freeSpeak && onMic) releaseSelf()
      return
    }
    try {
      const s = await media.publish({ audio: true, video: camOn })
      onLocalStream(s)
      setMicOn(true)
      // 自由发言模式下，开麦就是「我要说话」——
      // 必须告诉服务端，否则别人界面上看不出他在发言、主持人也没法静音他。
      // 麦序模式不在这里请求：那边要走「申请→排队→主持人给麦」。
      if (freeSpeak && !onMic) requestMic()
    } catch (e) {
      // 把真实原因带出来。原先一律说「请检查浏览器权限」，
      // 而最常见的原因其实是没走 HTTPS —— 照那句话排查永远查不到。
      useRoomStore.getState().setToast(
        e instanceof Error ? e.message : '无法访问麦克风，请检查浏览器权限')
    }
  }

  /** 开关摄像头推流。 */
  const toggleCam = async () => {
    if (!media) return
    if (camOn) {
      await media.unpublish(['video'])
      onLocalStream(null)
      setCamOn(false)
      return
    }
    try {
      const s = await media.publish({ audio: micOn, video: true })
      onLocalStream(s)
      setCamOn(true)
    } catch (e) {
      useRoomStore.getState().setToast(
        e instanceof Error ? e.message : '无法访问摄像头，请检查浏览器权限')
    }
  }

  /** 主动下麦。 */
  const releaseSelf = () => {
    if (selfId >= 0) releaseMic(selfId)
  }

  // 手机 App：进教室开一个系统音频会话（切后台不断音、通话模式、来电自动静音）。
  // 网页版与桌面端里这个 hook 是空转。
  const { interrupted } = useAudioSession(media, room?.name ?? '', micOn)

  const toggleMute = () => {
    media?.setMuted(!muted)
    setMuted(!muted)
  }

  // 按住空格临时说话：松开就恢复原状态。
  // 只在已开麦时生效 —— 没开麦时按空格去申请麦序会打断别人。
  const pushToTalk = (talking: boolean) => {
    if (!media || !micOn) return
    media.setMuted(!talking)
    setMuted(!talking)
  }

  // 进教室后按准备页的选择自动开一次。只跑一次：
  // 之后用户在工具栏上的开关必须说了算，否则一重渲染就被打回原样。
  const startedRef = useRef(false)
  useEffect(() => {
    if (startedRef.current || !media || !startWith) return
    startedRef.current = true
    // 必须串行：两个 toggle 各自会 getUserMedia，并发时都看到「还没开」，
    // 于是各采一路，音频被推两遍，对端听到两份自己的声音。
    void (async () => {
      if (startWith.micOn) await toggleMic()
      if (startWith.camOn) await toggleCam()
    })()
    // toggleMic/toggleCam 每次渲染都是新函数，进依赖数组会让这段反复触发。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media, startWith])

  useHotkeys({
    toggleMic: mediaBlocked ? undefined : () => void toggleMic(),
    toggleCam: mediaBlocked ? undefined : () => void toggleCam(),
    // 只有「已开麦且当前静音」才需要按住说话，否则空格没有意义。
    pushToTalk: micOn && muted ? pushToTalk : undefined,
    // 下面几个键位照腾讯会议。没权限或手机布局时传 undefined，按键静默忽略。
    toggleShare: canShare && !mobileBar ? () => (isSharing ? stopShare() : void startShare()) : undefined,
    toggleChat: mobileBar ? mobileSheets!.openChat : desktopSide?.openChat,
    toggleMembers: mobileBar ? mobileSheets!.openMembers : desktopSide?.toggle,
    toggleHand: canSpeak && freeSpeak ? () => raiseHand(!handRaised) : undefined,
    // 全屏由视频区自己实现（要拿到那个 DOM 节点），这里只发一个自定义事件过去。
    toggleFull: () => window.dispatchEvent(new CustomEvent('whatylive.toggleFull')),
  })

  const lectureMode = !!room && (room.flags & RoomFlag.StateLectureMode) !== 0

  const locked = !!room && (room.flags & RoomFlag.StateLocked) !== 0
  const waitingRoom = !!room && (room.flags & RoomFlag.StateWaitingRoom) !== 0
  const iAmOwner = !!room && room.ownerId === selfId
  // 投票联席主持也能发起（服务端就是按 IsOp 判的），别只认房主。
  const iAmHost = !!me && hasPerm(me.prop, Perm.Op)

  /**
   * 从「更多」抽屉里再打开另一个抽屉（成员）：要等「更多」那层 history 弹掉之后再开。
   * 抽屉关闭时的 history.back() 是异步的 —— 如果立刻打开成员抽屉，它先 pushState，
   * 随后那个 back 把刚压的成员那层弹掉，成员抽屉一闪就没了。
   */
  const openAfterMenu = (open: () => void) => {
    setMoreOpen(false)
    let done = false
    const go = () => { if (!done) { done = true; window.removeEventListener('popstate', go); open() } }
    window.addEventListener('popstate', go)
    window.setTimeout(go, 350) // 没有压过 history 的情况（隐私模式等）也要能打开
  }
  const endRoom = useRoomStore((s) => s.endRoom)

  // 底部主操作栏。
  // 最常用的三件事（麦克风、摄像头、发言）放中间且最醒目，
  // 退出教室单独靠右并涂红 —— 误触的代价最大的那个，必须离得最远。
  return (
    <div className="toolbar">
      {devOpen && <DeviceSettings onClose={() => setDevOpen(false)} />}
      {hkOpen && <HotkeyHelp onClose={() => setHkOpen(false)} />}
      {/* 来电打断期间给一句话，否则用户只知道「突然听不见了」，会以为是网络或应用坏了。 */}
      {interrupted && (
        <div className="tb-interrupt" role="status">通话中，已暂时关闭你的麦克风；挂断后自动恢复</div>
      )}
        <div className="tb-main">
          <button
            className={`tb-btn${micOn ? ' on' : ' off'}`}
            onClick={toggleMic}
            disabled={!!mediaBlocked}
            title={mediaBlocked ?? '快捷键 Ctrl/⌘ + D'}
          >
            <span className="tb-ico">{micOn ? <IcMic /> : <IcMicOff />}</span>
            {/* data-short 是窄屏用的短文案（见 styles.css 的 640px 断点）。
                手机上「开启麦克风」五个字排不下，会被截成「开启麦…」。
                短文案只经 ::before 显示，不进 textContent —— 自动化测试
                照旧按完整文字找按钮，不受影响。 */}
            {/* 文案与腾讯会议一致：静音 / 解除静音，不说「关闭麦克风」 */}
            <span className="tb-txt">{micOn ? '静音' : '解除静音'}</span>
          </button>

          {micOn && (
            <button
              /* 手机上只留一个麦克风按钮：这个软静音和上面的按钮都显示「静音」，两个并排会让人分不清 */
              className={`tb-btn hidden-mobile${muted ? ' warn' : ''}`}
              onClick={toggleMute}
              title={muted ? '按住空格键可临时说话' : undefined}
            >
              <span className="tb-ico">{muted ? <IcSpeakerOff /> : <IcSpeaker />}</span>
              <span className="tb-txt">{muted ? '恢复说话' : '暂时闭麦'}</span>
            </button>
          )}

          <button
            className={`tb-btn${camOn ? ' on' : ' off'}`}
            onClick={toggleCam}
            disabled={!!mediaBlocked}
            title={mediaBlocked ?? '快捷键 Ctrl/⌘ + E'}
          >
            <span className="tb-ico">{camOn ? <IcCam /> : <IcCamOff />}</span>
            <span className="tb-txt" data-short={camOn ? '关视频' : '开视频'}>
              {camOn ? '停止视频' : '开启视频'}
            </span>
          </button>

          {/*
            自由发言模式（对齐腾讯会议）：麦克风开关就是发言开关，
            这里只留「举手」当示意，不阻塞发言。
            主持人模式（老系统麦序）：保持「申请发言 → 排队 → 主持人给麦」。
          */}
          {canSpeak && !freeSpeak && !iAmHost && (
            <button
              className={`tb-btn${onMic ? ' on' : ''}`}
              onClick={onMic ? releaseSelf : requestMic}
            >
              <span className="tb-ico"><IcHand /></span>
              <span className="tb-txt">{onMic ? '结束发言' : '申请发言'}</span>
            </button>
          )}
          {canSpeak && freeSpeak && (
            <button
              className={`tb-btn${handRaised ? ' on raised' : ''}`}
              onClick={() => {
                // 手机上举手给一下震动：屏幕小、图标又不大，
                // 没有反馈的话学员会怀疑点没点上而连点两次 —— 那就把手又放下了。
                if (mobileBar && !handRaised) navigator.vibrate?.(30)
                raiseHand(!handRaised)
              }}
              title={`${handRaised ? '放下手' : '举手示意，不影响你现在能否说话'}（Ctrl/⌘ + Shift + K）`}
            >
              <span className="tb-ico"><IcHand /></span>
              <span className="tb-txt">{handRaised ? (mobileBar ? '已举手' : '放下手') : '举手'}</span>
            </button>
          )}

          {!mobileBar && <ReactionPicker />}

          {/* 手机：聊天入口带未读角标。 */}
          {mobileBar && (
            <button className="tb-btn" onClick={mobileSheets!.openChat} title="打开交流区">
              <span className="tb-ico"><IcChat /></span>
              <span className="tb-txt">聊天</span>
              {mobileSheets!.unread.count > 0 && (
                <em className={`tb-badge${mobileSheets!.unread.mention ? ' mention' : ''}`}>
                  {mobileSheets!.unread.mention ? '@' : mobileSheets!.unread.count > 99 ? '99+' : mobileSheets!.unread.count}
                </em>
              )}
            </button>
          )}

          {/* 手机浏览器没有 getDisplayMedia，共享按钮点了也没用，不显示。 */}
          {canShare && !mobileBar && (
            <>
              {isSharing ? (
                <>
                  <button
                    className={`tb-btn${sharePaused ? ' on' : ''}`}
                    onClick={() => pauseShare(!sharePaused)}
                    title="暂停后对方看到的画面会定格，切到私密内容时先按这里"
                  >
                    <span className="tb-ico">{sharePaused ? <IcPlay /> : <IcPause />}</span>
                    <span className="tb-txt">{sharePaused ? '继续共享' : '暂停共享'}</span>
                  </button>
                  <button className="tb-btn warn" onClick={stopShare} title="快捷键 Ctrl/⌘ + Shift + S">
                    <span className="tb-ico"><IcStop /></span>
                    <span className="tb-txt">停止共享</span>
                  </button>
                </>
              ) : (
                <button className="tb-btn" onClick={() => void startShare()} title="快捷键 Ctrl/⌘ + Shift + S">
                  <span className="tb-ico"><IcShare /></span>
                  <span className="tb-txt">共享屏幕</span>
                </button>
              )}
            </>
          )}

          {canRecord && !mobileBar && (
            <button
              className={`tb-btn${recording.active ? ' rec' : ''}`}
              onClick={() => (recording.active ? stopRecord() : startRecord(0))}
            >
              <span className="tb-ico">{recording.active ? <IcRecordStop /> : <IcRecord />}</span>
              <span className="tb-txt">
                {recording.active ? `停止录制 ${formatDuration(recording.elapsed)}` : '开始录制'}
              </span>
            </button>
          )}

          {desktopSide && (
            <>
              <button className={`tb-btn${desktopSide.open ? ' on' : ''}`} onClick={desktopSide.toggle} title="显示或收起成员与交流（Ctrl/⌘ + U）">
                <span className="tb-ico"><IcUsers /></span>
                <span className="tb-txt">成员 {desktopSide.memberCount}</span>
              </button>
              <button className={`tb-btn${desktopSide.open ? ' on' : ''}`} onClick={desktopSide.openChat} title="打开交流（Ctrl/⌘ + Shift + H）">
                <span className="tb-ico"><IcChat /></span>
                <span className="tb-txt">聊天</span>
                {!desktopSide.open && desktopSide.unread.count > 0 && (
                  <span className={`tb-badge${desktopSide.unread.mention ? ' mention' : ''}`}>
                    {desktopSide.unread.mention ? '@' : desktopSide.unread.count > 99 ? '99+' : desktopSide.unread.count}
                  </span>
                )}
              </button>
            </>
          )}

          {/* 用得少的收进「更多」，别让它们和麦克风抢注意力。 */}
          <div className="tb-more-wrap">
            <button
              className={`tb-btn${moreOpen ? ' on' : ''}`}
              onClick={() => setMoreOpen((v) => !v)}
            >
              <span className="tb-ico"><IcMore /></span>
              <span className="tb-txt">更多</span>
            </button>
            {/* 手机上「更多」是底部抽屉里的宫格（腾讯会议手机端的样子）；桌面是按钮上方的浮层。 */}
            {moreOpen && (() => {
              const menu = (
                <div className={`tb-more${mobileBar ? ' more-grid' : ''}`} onClick={() => setMoreOpen(false)}>
                {mobileBar && (
                  <>
                    <button onClick={() => openAfterMenu(mobileSheets!.openMembers)}>
                      在场成员 <span className="mic-num">{mobileSheets!.memberCount}</span>
                    </button>
                    {/* 表情直接摊成一行，少一次点击。 */}
                    <div className="react-row" onClick={(e) => e.stopPropagation()}>
                      {EMOJIS.map((em) => (
                        <button key={em} className="react-item" onClick={() => { sendReaction(em); setMoreOpen(false) }}>
                          {em}
                        </button>
                      ))}
                    </div>
                    {canRecord && (
                      <button
                        className={recording.active ? 'on' : ''}
                        onClick={() => (recording.active ? stopRecord() : startRecord(0))}
                      >
                        {recording.active ? `停止录制 ${formatDuration(recording.elapsed)}` : '开始录制'}
                      </button>
                    )}
                  </>
                )}
                {iAmHost && (
                  <button onClick={() => setMcOpen(true)} title="一次设置全体学员的发言、视频、共享等权限">
                    会议管控…
                  </button>
                )}
                {iAmHost && (
                  <button onClick={() => setPollOpen(true)} title="向全体发起一次投票">
                    发起投票…
                  </button>
                )}
                {iAmHost && (
                  <button onClick={() => setAttendOpen(true)} title="向在场学员发起一次签到，记录会保存下来">
                    发起签到…
                  </button>
                )}
                {iAmHost && (
                  <button onClick={() => setGroupOpen(true)} title="把学员分成几个小组分别讨论">
                    分组讨论…
                  </button>
                )}
                {iAmHost && captionable && (
                  <button
                    className={captionOn ? 'on' : ''}
                    onClick={() => toggleCaption(!captionOn)}
                    title="开启后大家说的话会实时转成字幕显示在屏幕下方"
                  >
                    {captionOn ? '关闭字幕' : '开启实时字幕'}
                  </button>
                )}
                {iAmHost && <div className="tb-sep" />}
                <button onClick={() => setInfoOpen(true)}>教室信息与邀请…</button>
                <button onClick={() => setHkOpen(true)}>快捷键…</button>
                <button onClick={() => setDevOpen(true)}>音视频设置…</button>
                <button
                  onClick={() => {
                    const next = window.prompt(
                      '写一句介绍自己的话，会显示在名字后面',
                      me?.info ?? '',
                    )
                    if (next !== null) setSelfInfo(next.trim())
                  }}
                >
                  我的简介
                </button>
                <button className={away ? 'on' : ''} onClick={toggleAway}>
                  {away ? '我回来了' : '暂离'}
                </button>
                {canSetMicNum && (
                  <label className="mic-num">
                    麦位
                    <select
                      value={room?.micNum ?? 8}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setMicNum(Number(e.target.value))}
                    >
                      {[1, 2, 4, 6, 8].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {canSetRoom && (
                  <button
                    className={lectureMode ? 'on' : ''}
                    onClick={() =>
                      setRoomFlags(RoomFlag.StateLectureMode, lectureMode ? 'remove' : 'add')
                    }
                    title="讲课模式下仅主持人与持麦者可发言"
                  >
                    讲课模式
                  </button>
                )}
                {canSetRoom && (
                  <button
                    className={freeSpeak ? 'on' : ''}
                    onClick={() =>
                      setRoomFlags(RoomFlag.StateFreeSpeak, freeSpeak ? 'remove' : 'add')
                    }
                    title="自由发言：人人可自行开麦（像腾讯会议）。关闭则回到申请发言、由你逐个给麦"
                  >
                    {freeSpeak ? '改为申请发言' : '改为自由发言'}
                  </button>
                )}
                {canSetRoom && (
                  <button
                    className={waitingRoom ? 'on' : ''}
                    onClick={() =>
                      setRoomFlags(RoomFlag.StateWaitingRoom, waitingRoom ? 'remove' : 'add')
                    }
                    title="开启后学员进教室要先经你允许；主持人自己不受影响"
                  >
                    {waitingRoom ? '关闭等候室' : '开启等候室'}
                  </button>
                )}
                {canSetRoom && (
                  <button
                    className={locked ? 'on' : ''}
                    onClick={() => setRoomFlags(RoomFlag.StateLocked, locked ? 'remove' : 'add')}
                    title="锁定后新人进不来，已在教室里的不受影响"
                  >
                    {locked ? '解除锁定' : '锁定教室'}
                  </button>
                )}
                {mobileBar && (
                  <button className="danger" onClick={() => setLeaveOpen(true)}>
                    退出教室…
                  </button>
                )}
                </div>
              )
              return mobileBar ? (
                <BottomSheet open title="更多" onClose={() => setMoreOpen(false)}>{menu}</BottomSheet>
              ) : menu
            })()}
          </div>
        </div>

        <div className={`tb-leave-wrap${mobileBar ? ' hidden-mobile' : ''}`}>
          <button className="tb-leave" onClick={() => setLeaveOpen((v) => !v)} title="退出教室">
            <span className="tb-ico"><IcHangup /></span>
            <span className="tb-txt">退出教室</span>
          </button>
          {leaveOpen && (
            <div className="leave-pop" role="dialog" aria-label="确认退出">
              <button className="danger" onClick={onLeave}>离开教室</button>
              {iAmOwner && (
                <button
                  className="danger end"
                  title="全员退出教室"
                  onClick={() => {
                    if (!window.confirm('结束本节课？所有人都会被请出教室。')) return
                    endRoom()
                  }}
                >
                  结束课程（全员）
                </button>
              )}
              <button className="quiet" onClick={() => setLeaveOpen(false)}>取消</button>
            </div>
          )}
        </div>
        {infoOpen && <RoomInfo roomPassword={roomPassword} onClose={() => setInfoOpen(false)} />}
        {pollOpen && <PollCompose onClose={() => setPollOpen(false)} />}
        {attendOpen && <AttendStartDialog onClose={() => setAttendOpen(false)} />}
        {groupOpen && <GroupCompose onClose={() => setGroupOpen(false)} />}
        {mcOpen && <MeetingControl onClose={() => setMcOpen(false)} />}
      </div>
  )
}

/** 毫秒格式化成 MM:SS，用于录制时长显示。 */
function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}


/**
 * 白板工具条。
 *
 * 单独一个组件、渲染在白板正上方，而不是塞进底部操作栏 ——
 * 画笔、颜色、翻页属于「正在画的这块板」，手要在板和工具之间来回，
 * 离得越近越顺手；而麦克风、录制是整堂课的开关，位置固定在底部才不容易误触。
 */
export function WhiteboardTools({
  client,
  onImportDoc,
}: {
  client: Client
  onImportDoc: () => void
}) {
  const selfId = useRoomStore((s) => s.selfId)
  const members = useRoomStore((s) => s.members)
  const me = members.get(selfId)
  const canDrawWB = !!me && (hasPerm(me.prop, Perm.Whiteboard) || hasPerm(me.prop, Perm.OpWB))
  const mobile = useMobileLayout()
  // 加页 / 清空本页 / 导入课件服务端要 PermDelWB 或 PermOpWB（见 room/whiteboard_page.go）。
  // 前端不跟着隐藏的话，学员点了只会收到一句错误 —— 给个点了没反应的按钮
  // 比没有这个按钮更糟。
  const canManageWB = !!me && (hasPerm(me.prop, Perm.OpWB) || hasPerm(me.prop, Perm.DelWB))

  const tool = useWhiteboardStore((s) => s.tool)
  const setTool = useWhiteboardStore((s) => s.setTool)
  const style = useWhiteboardStore((s) => s.style)
  const setStyle = useWhiteboardStore((s) => s.setStyle)
  const allPages = useWhiteboardStore((s) => s.pages)
  // 页导航里不能出现共享批注层：它是共享期间临时追加的一页，
  // 算进「1 / N」会让老师以为课件多了一页，翻过去看到的却是别人画在共享画面上的东西。
  const pages = allPages.filter((p) => !isAnnotPage(p))
  const currentPage = useWhiteboardStore((s) => s.currentPage)
  const gotoPage = useWhiteboardStore((s) => s.gotoPage)
  const addPage = useWhiteboardStore((s) => s.addPage)
  const clearPage = useWhiteboardStore((s) => s.clearPage)
  const renamePage = useWhiteboardStore((s) => s.renamePage)
  const lockObject = useWhiteboardStore((s) => s.lockObject)
  const deleteSelected = useWhiteboardStore((s) => s.deleteSelected)
  const clearSelection = useWhiteboardStore((s) => s.clearSelection)
  const selectedIds = useWhiteboardStore((s) => s.selectedIds)

  if (!canDrawWB) return null

  // 手机上不做作画：屏幕太小、手指精度不够，做了也没人用。
  // 整条工具条 60px，是手机首屏的 7%，只给能翻页的人留一条最薄的翻页条。
  if (mobile) {
    if (!canManageWB) return null
    return (
      <div className="wb-tools compact">
        <span className="page-nav">
          <button disabled={currentPage <= 0} onClick={() => gotoPage(currentPage - 1)}>‹</button>
          <span className="page-label">{currentPage + 1} / {pages.length}</span>
          <button disabled={currentPage >= pages.length - 1} onClick={() => gotoPage(currentPage + 1)}>›</button>
        </span>
      </div>
    )
  }

  return (
    <div className="wb-tools">
        <div className="tb-group">
          {TOOLS.map((t) => (
            <button
              key={t.id}
              className={`tb-tool${tool === t.id ? ' on' : ''}`}
              onClick={() => setTool(t.id)}
              title={t.label}
            >
              <span className="tb-ico"><t.Icon /></span>
              <span className="tb-txt">{t.label}</span>
            </button>
          ))}
        </div>

        <div className="tb-group">
          {PALETTE.map((c) => (
            <button
              key={c}
              className={`swatch${style.lineColor === c ? ' on' : ''}`}
              style={{ background: c }}
              title={c}
              onClick={() => setStyle({ lineColor: c })}
            />
          ))}
          <input
            className="tb-width"
            type="range"
            min={1}
            max={12}
            value={Math.round((style.lineWidth ?? 0.004) * 1000)}
            title="线宽"
            onChange={(e) => setStyle({ lineWidth: Number(e.target.value) / 1000 })}
          />
        </div>

        {selectedIds.length > 0 && (
          <div className="tb-group">
            <button
              onClick={() => {
                selectedIds.forEach((id) => lockObject(client, id, true))
                clearSelection()
              }}
              title="锁住的图元别人改不了、删不掉"
            >
              锁定选中 ({selectedIds.length})
            </button>
            <button onClick={() => selectedIds.forEach((id) => lockObject(client, id, false))}>
              解锁
            </button>
            <button className="warn" onClick={() => deleteSelected(client)}>
              删除选中
            </button>
          </div>
        )}

        <div className="tb-group tb-pages">
          <span className="page-nav">
            <button disabled={currentPage <= 0} onClick={() => gotoPage(currentPage - 1)}>
              ‹
            </button>
            <span
              className={canDrawWB ? 'page-label editable' : 'page-label'}
              title={canDrawWB ? '点击给本页命名' : undefined}
              onClick={() => {
                if (!canDrawWB) return
                const cur = pages.find((p) => p.index === currentPage)?.title ?? ''
                const next = window.prompt('本页标题', cur)
                if (next !== null) renamePage(client, next)
              }}
            >
              {currentPage + 1} / {pages.length}
              {pages.find((p) => p.index === currentPage)?.title
                ? ` · ${pages.find((p) => p.index === currentPage)!.title}`
                : ''}
            </span>
            <button
              disabled={currentPage >= pages.length - 1}
              onClick={() => gotoPage(currentPage + 1)}
            >
              ›
            </button>
          </span>
          {canManageWB && <button onClick={() => addPage(client)}>加页</button>}
          {canManageWB && <button onClick={() => clearPage(client)}>清空本页</button>}
          {canManageWB && (
            <button onClick={onImportDoc} title="导入 PDF 或图片作为课件">
              导入课件
            </button>
          )}
        </div>
      </div>
  )
}
